import React, { useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, AlertCircle, Loader } from 'lucide-react';
import './CorrectiveActionsPanel.css';

const CA_STATUS_OPTIONS = [
  { value: 'PLANNED', label: 'Planned', color: '#6c757d' },
  { value: 'IN_PROGRESS', label: 'In Progress', color: '#0d6efd' },
  { value: 'COMPLETED', label: 'Completed', color: '#198754' },
  { value: 'OVERDUE', label: 'Overdue', color: '#dc3545' },
];

/**
 * CorrectiveActionsPanel Component
 * Manages corrective actions for a finding
 */
const CorrectiveActionsPanel = ({
  findingId = null,
  correctiveActions = [],
  onAdd = () => {},
  onUpdate = () => {},
  onDelete = () => {},
  isLoading = false,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCA, setEditingCA] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    ownerName: '',
    targetDate: '',
    status: 'PLANNED',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      description: '',
      ownerName: '',
      targetDate: '',
      status: 'PLANNED',
    });
    setErrors({});
    setEditingCA(null);
    setShowForm(false);
  }, []);

  // Load CA data for editing
  const handleEdit = useCallback((ca) => {
    setEditingCA(ca);
    setFormData({
      description: ca.description || '',
      ownerName: ca.ownerName || '',
      targetDate: ca.targetDate || '',
      status: ca.status || 'PLANNED',
    });
    setShowForm(true);
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
    }
    if (!formData.targetDate) {
      newErrors.targetDate = 'Target date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle form submit
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingCA) {
        await onUpdate(editingCA.id, formData);
      } else {
        await onAdd(formData);
      }
      resetForm();
    } catch (error) {
      console.error('Error submitting corrective action:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [editingCA, formData, validateForm, onUpdate, onAdd, resetForm]);

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) return;

    setIsSubmitting(true);
    try {
      await onDelete(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting corrective action:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [deleteConfirm, onDelete]);

  // Calculate days remaining
  const getDaysRemaining = (targetDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const option = CA_STATUS_OPTIONS.find((opt) => opt.value === status);
    return option?.color || '#6c757d';
  };

  // Check if CA is overdue
  const isOverdue = (ca) => {
    if (ca.status === 'COMPLETED' || ca.status === 'OVERDUE') return false;
    const daysRemaining = getDaysRemaining(ca.targetDate);
    return daysRemaining < 0;
  };

  return (
    <div className="corrective-actions-panel">
      {/* Header */}
      <div className="ca-header">
        <h5 className="mb-0">Corrective Actions</h5>
        {!showForm && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setShowForm(true)}
            disabled={isLoading}
          >
            <Plus size={16} className="me-1" />
            Add Action
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="ca-form card card-body bg-light mb-3">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="ca-description" className="form-label">
                    Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="ca-description"
                    className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                    rows="2"
                    placeholder="Corrective action description..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={isSubmitting}
                  />
                  {errors.description && (
                    <div className="invalid-feedback d-block">{errors.description}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="ca-owner" className="form-label">
                    Owner <span className="text-danger">*</span>
                  </label>
                  <input
                    id="ca-owner"
                    type="text"
                    className={`form-control ${errors.ownerName ? 'is-invalid' : ''}`}
                    placeholder="Owner name..."
                    value={formData.ownerName}
                    onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                    disabled={isSubmitting}
                  />
                  {errors.ownerName && (
                    <div className="invalid-feedback d-block">{errors.ownerName}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="ca-target-date" className="form-label">
                    Target Date <span className="text-danger">*</span>
                  </label>
                  <input
                    id="ca-target-date"
                    type="date"
                    className={`form-control ${errors.targetDate ? 'is-invalid' : ''}`}
                    value={formData.targetDate}
                    onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                    disabled={isSubmitting}
                  />
                  {errors.targetDate && (
                    <div className="invalid-feedback d-block">{errors.targetDate}</div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="ca-status" className="form-label">
                    Status
                  </label>
                  <select
                    id="ca-status"
                    className="form-select"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    disabled={isSubmitting}
                  >
                    {CA_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-sm btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={14} className="me-1 spinner-icon" />
                    Saving...
                  </>
                ) : (
                  editingCA ? 'Update' : 'Add'
                )}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={resetForm}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="alert alert-danger mb-3">
          <div className="d-flex align-items-start gap-2">
            <AlertCircle size={20} className="mt-1 flex-shrink-0" />
            <div className="flex-grow-1">
              <p className="mb-2">Delete this corrective action?</p>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-danger"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CA List */}
      {correctiveActions.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th>Description</th>
                <th>Owner</th>
                <th>Target Date</th>
                <th>Status</th>
                <th>Days Remaining</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {correctiveActions.map((ca) => {
                const daysRemaining = getDaysRemaining(ca.targetDate);
                const overdue = isOverdue(ca);

                return (
                  <tr key={ca.id} className={overdue ? 'table-danger' : ''}>
                    <td>
                      <small>{ca.description}</small>
                    </td>
                    <td>
                      <small>{ca.ownerName || 'N/A'}</small>
                    </td>
                    <td>
                      <small>
                        {ca.targetDate ? new Date(ca.targetDate).toLocaleDateString() : 'N/A'}
                      </small>
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{ backgroundColor: getStatusColor(ca.status) }}
                      >
                        {ca.status}
                      </span>
                    </td>
                    <td>
                      <small className={overdue ? 'text-danger fw-bold' : ''}>
                        {overdue ? 'OVERDUE' : `${daysRemaining} days`}
                      </small>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => handleEdit(ca)}
                          disabled={isLoading}
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => setDeleteConfirm(ca)}
                          disabled={isLoading}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="alert alert-info mb-0">
          <p className="mb-0">No corrective actions yet. Click "Add Action" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default CorrectiveActionsPanel;
